import ExcelJS from "exceljs";

/** Tên sheet dữ liệu — backend import ưu tiên đọc sheet này */
export const LESSON_IMPORT_DATA_SHEET = "Danh sách";

const thinBorder = {
  top: { style: "thin", color: { argb: "FFD1D5DB" } },
  left: { style: "thin", color: { argb: "FFD1D5DB" } },
  bottom: { style: "thin", color: { argb: "FFD1D5DB" } },
  right: { style: "thin", color: { argb: "FFD1D5DB" } },
};

/** Cột nhập bài học (metadata) */
export const LESSON_IMPORT_COLS = [
  {
    header: "Thứ tự",
    width: 12,
    required: false,
    format: "Số nguyên ≥ 0",
    example: "1",
    note: "Tuỳ chọn. Để trống = hệ thống tự gán tiếp theo (sau thứ tự lớn nhất hiện có của khóa học).",
  },
  {
    header: "Tên bài học",
    width: 40,
    required: true,
    format: "Văn bản, không trùng trong cùng khóa học",
    example: "Bài 1: Giới thiệu khóa học",
    note: "Bắt buộc. Trùng tên (không phân biệt hoa/thường) với bài đã có → dòng bị bỏ qua.",
  },
  {
    header: "Mô tả",
    width: 48,
    required: false,
    format: "Văn bản tự do",
    example: "Mục tiêu và lộ trình học",
    note: "Tuỳ chọn. Mô tả ngắn nội dung / mục tiêu bài học.",
  },
];

const DATA_ENTRY_ROWS = 50;

const SAMPLE_ROWS = [
  [1, "Bài 1: Giới thiệu khóa học", "Mục tiêu và lộ trình học"],
  [2, "Bài 2: Kiến thức nền", "Ôn tập kiến thức cần có trước khi học"],
  [3, "Bài 3: Thực hành", "Bài tập áp dụng"],
];

function styleTitleCell(cell, { size = 15, color = "FF7F1D1D", bg = "FFFEE2E2" } = {}) {
  cell.font = { bold: true, size, color: { argb: color } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
  cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  cell.border = thinBorder;
}

function styleSectionTitle(cell) {
  cell.font = { bold: true, size: 12, color: { argb: "FFB91C1C" } };
  cell.alignment = { vertical: "middle", wrapText: true };
}

function styleBodyCell(cell, { bold = false, italic = false, bg = null } = {}) {
  cell.font = { size: 11, bold, italic, color: { argb: "FF374151" } };
  cell.alignment = { vertical: "top", wrapText: true };
  if (bg) {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
  }
  cell.border = thinBorder;
}

export function sanitizeFileNamePart(str) {
  return String(str || "")
    .trim()
    .replace(/[/\\?%*:|"<>]/g, "-")
    .replace(/\s+/g, " ");
}

/**
 * Đặt tên file theo quy chuẩn: [Temp]_[Tên môn học]_[Tên khóa học].xlsx
 */
export function buildLessonImportFileName(subjectName, courseName) {
  const cleanSub = sanitizeFileNamePart(subjectName);
  const cleanCourse = sanitizeFileNamePart(courseName);

  if (cleanSub && cleanCourse) {
    return `[Temp]_[${cleanSub}]_[${cleanCourse}].xlsx`;
  }
  if (cleanCourse) {
    return `[Temp]_[${cleanCourse}].xlsx`;
  }
  if (cleanSub) {
    return `[Temp]_[${cleanSub}].xlsx`;
  }
  return `[Temp]_[Môn học]_[Khóa học].xlsx`;
}

function buildGuideSheet(wb, { subjectName = "", courseName = "" } = {}) {
  const ws = wb.addWorksheet("Hướng dẫn", {
    views: [{ state: "frozen", ySplit: 1 }],
    properties: { defaultRowHeight: 18 },
  });
  const ncol = 6;
  ws.columns = [
    { width: 4 },
    { width: 22 },
    { width: 12 },
    { width: 40 },
    { width: 36 },
    { width: 52 },
  ];

  let r = 1;
  ws.mergeCells(r, 1, r, ncol);
  const t = ws.getCell(r, 1);
  t.value = "HƯỚNG DẪN IMPORT BÀI HỌC (E-LEARNING)";
  styleTitleCell(t);
  ws.getRow(r).height = 36;
  r += 1;

  ws.mergeCells(r, 1, r, ncol);
  const sub = ws.getCell(r, 1);
  const contextInfo =
    subjectName && courseName
      ? `Môn học: ${subjectName}  |  Khóa học: ${courseName}`
      : courseName
        ? `Khóa học: ${courseName}`
        : "IMA Vietnam ERP — Nhập hàng loạt bài học";
  sub.value = `${contextInfo} — Phiên bản biểu mẫu: 2026.`;
  sub.font = { italic: true, size: 10, color: { argb: "FF6B7280" } };
  sub.alignment = { horizontal: "center", wrapText: true };
  r += 2;

  const addSection = (title, lines) => {
    ws.mergeCells(r, 1, r, ncol);
    const c = ws.getCell(r, 1);
    c.value = title;
    styleSectionTitle(c);
    ws.getRow(r).height = 22;
    r += 1;
    for (const line of lines) {
      ws.mergeCells(r, 1, r, ncol);
      const lc = ws.getCell(r, 1);
      lc.value = line;
      styleBodyCell(lc);
      ws.getRow(r).height = line.length > 90 ? 38 : 20;
      r += 1;
    }
    r += 1;
  };

  addSection("I. QUY TRÌNH TRÊN HỆ THỐNG", [
    "Bước 1 — Mở «Bài học» (E-learning) → Bấm nút «Nhập Excel».",
    "Bước 2 — Chọn Môn học và Khóa học cần nhập bài trong form.",
    "Bước 3 — Tải file mẫu này, mở sheet «Danh sách» và nhập từng dòng = một bài học.",
    "Bước 4 — Lưu file dạng .xlsx và tải lên hệ thống.",
    "Bước 5 — Hệ thống tạo bài học (metadata). Tài liệu / file bài giảng gắn sau tại «Sửa bài học».",
  ]);

  addSection("II. CÁCH NHẬP SHEET «DANH SÁCH»", [
    "• Sheet «Danh sách» — giữ nguyên dòng 1 là tiêu đề cột, không xoá, không đổi tên cột.",
    "• Nhập từ dòng 2 trở xuống. Dòng trống (không có Tên bài học) sẽ bỏ qua.",
    "• Cột có dấu * trên tiêu đề là bắt buộc (Tên bài học *).",
    "• Có thể xoá các dòng mẫu màu vàng rồi nhập dữ liệu thật; hoặc ghi đè nội dung mẫu.",
    "• Không gộp ô, không chèn dòng phía trên tiêu đề, không đổi tên sheet «Danh sách».",
    "• File .xls / .csv vẫn có thể import, nhưng khuyến nghị dùng mẫu .xlsx này.",
  ]);

  ws.mergeCells(r, 1, r, ncol);
  ws.getCell(r, 1).value = "III. BẢNG MÔ TẢ CỘT";
  styleSectionTitle(ws.getCell(r, 1));
  ws.getRow(r).height = 22;
  r += 1;

  const hdr = ws.getRow(r);
  ["STT", "Tên cột", "Bắt buộc", "Định dạng / Giá trị", "Ví dụ", "Ghi chú"].forEach(
    (label, idx) => {
      const c = hdr.getCell(idx + 1);
      c.value = label;
      c.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFB91C1C" } };
      c.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      c.border = thinBorder;
    }
  );
  hdr.height = 26;
  r += 1;

  LESSON_IMPORT_COLS.forEach((col, idx) => {
    const row = ws.getRow(r);
    const vals = [
      idx + 1,
      col.required ? `${col.header} *` : col.header,
      col.required ? "Có" : "Không",
      col.format,
      col.example,
      col.note,
    ];
    vals.forEach((val, ci) => {
      const c = row.getCell(ci + 1);
      c.value = val;
      styleBodyCell(c, { bg: idx % 2 === 0 ? "FFF9FAFB" : "FFFFFFFF" });
      c.alignment = {
        vertical: "top",
        wrapText: true,
        horizontal: ci === 0 ? "center" : "left",
      };
    });
    row.height = 44;
    r += 1;
  });
  r += 1;

  addSection("IV. VÍ DỤ", [
    "1 | Bài 1: Giới thiệu khóa học | Mục tiêu và lộ trình học",
    "2 | Bài 2: Kiến thức nền | Ôn tập kiến thức cần có trước khi học",
    "3 | Bài 3: Thực hành | Bài tập áp dụng",
  ]);

  addSection("V. LƯU Ý", [
    "• Hệ thống đọc sheet «Danh sách» (không đọc sheet Hướng dẫn).",
    "• Khóa học chọn trực tiếp trên màn hình import.",
    "• Chỉ tạo metadata bài học; ZIP bài giảng / PDF / tài liệu đính kèm bổ sung sau khi sửa bài.",
    "• Bài trùng tên trong cùng khóa học bị bỏ qua và báo lỗi theo dòng.",
    "• Thứ tự không hợp lệ (không phải số nguyên ≥ 0) → dòng lỗi, các dòng khác vẫn nhập được.",
  ]);

  return ws;
}

function buildDataSheet(wb) {
  const ws = wb.addWorksheet(LESSON_IMPORT_DATA_SHEET, {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  const headers = LESSON_IMPORT_COLS.map((c) =>
    c.required ? `${c.header} *` : c.header
  );

  const headerRow = ws.getRow(1);
  headers.forEach((h, idx) => {
    const col = idx + 1;
    const cell = headerRow.getCell(col);
    cell.value = h;
    cell.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFB91C1C" },
    };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = thinBorder;
    ws.getColumn(col).width = LESSON_IMPORT_COLS[idx].width;
  });
  headerRow.height = 32;

  ws.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: headers.length },
  };

  // Dòng mẫu (màu vàng nhạt, in nghiêng)
  SAMPLE_ROWS.forEach((vals, i) => {
    const rowNum = i + 2;
    const row = ws.getRow(rowNum);
    vals.forEach((val, ci) => {
      const cell = row.getCell(ci + 1);
      cell.value = val;
      cell.font = { size: 11, color: { argb: "FF92400E" }, italic: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFFBEB" },
      };
      cell.border = thinBorder;
      cell.alignment = {
        vertical: "middle",
        wrapText: true,
        horizontal: ci === 0 ? "center" : "left",
      };
    });
    row.height = 24;
  });

  // Các dòng trống để người dùng nhập liệu (nền trắng, viền mảnh)
  const startEmptyRow = 2 + SAMPLE_ROWS.length;
  for (let i = 0; i < DATA_ENTRY_ROWS; i += 1) {
    const rowNum = startEmptyRow + i;
    const row = ws.getRow(rowNum);
    for (let c = 1; c <= LESSON_IMPORT_COLS.length; c += 1) {
      const cell = row.getCell(c);
      cell.border = thinBorder;
      cell.alignment = {
        vertical: "middle",
        wrapText: true,
        horizontal: c === 1 ? "center" : "left",
      };
      cell.font = { size: 11, color: { argb: "FF111827" } };
    }
    row.height = 22;
  }

  return ws;
}

/**
 * Tạo blob file mẫu Excel import bài học.
 */
export async function buildLessonImportTemplateBlob({ subjectName = "", courseName = "" } = {}) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "IMA Vietnam ERP";
  wb.created = new Date();
  wb.properties.date1904 = false;

  buildGuideSheet(wb, { subjectName, courseName });
  buildDataSheet(wb);

  const buffer = await wb.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

/**
 * Tải biểu mẫu .xlsx với tên file [Temp]_[Tên môn học]_[Tên khóa học].xlsx
 */
export async function downloadLessonImportTemplate({ subjectName = "", courseName = "" } = {}) {
  const blob = await buildLessonImportTemplateBlob({ subjectName, courseName });
  const fileName = buildLessonImportFileName(subjectName, courseName);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
